import type { FulfillmentOrder, FulfillmentOrderLine } from "../types";
import { sumActiveFulfillmentLineAmounts, sumFulfillmentAmounts } from "./fulfillmentOrderEditorService";

/** 同一真实结算主体下的当前费用与已有协议。 */
export type ConfirmationSettlementGroup = {
  settlementSupplierId: string;
  settlementSupplierName: string;
  orders: FulfillmentOrder[];
  lines: FulfillmentOrderLine[];
  pendingLines: FulfillmentOrderLine[];
  pendingLineIds: string[];
  amount: string;
  pendingAmount: string;
};

/** 根据真实主体及协议身份汇总当前费用，不创建或修改协议。 */
export function buildConfirmationSettlementGroups({ workspaceStatus, lines, orders }: {
  workspaceStatus: string | undefined;
  lines: readonly FulfillmentOrderLine[];
  orders: readonly FulfillmentOrder[];
}): { groups: ConfirmationSettlementGroup[]; ungroupedLines: FulfillmentOrderLine[]; currentAmount: string } {
  const groupsBySupplier = new Map<string, ConfirmationSettlementGroup>();
  const ordersById = new Map(orders.map(order => [order.id, order]));
  const ungroupedLines: FulfillmentOrderLine[] = [];
  const getGroup = (id: string, name: string) => {
    let group = groupsBySupplier.get(id);
    if (!group) {
      group = { settlementSupplierId: id, settlementSupplierName: name, orders: [], lines: [], pendingLines: [], pendingLineIds: [], amount: "0.00", pendingAmount: "0.00" };
      groupsBySupplier.set(id, group);
    } else if (!group.settlementSupplierName) group.settlementSupplierName = name;
    return group;
  };
  for (const order of orders) getGroup(order.settlementSupplierId, order.settlementSupplierName).orders.push(order);
  for (const line of lines) {
    if (line.status !== "active") continue;
    if (line.fulfillmentOrderId) {
      const order = ordersById.get(line.fulfillmentOrderId);
      if (order) groupsBySupplier.get(order.settlementSupplierId)!.lines.push(line);
      else ungroupedLines.push(line);
    } else if (line.settlementSupplierId?.trim()) {
      const group = getGroup(line.settlementSupplierId, line.settlementSupplierName || "");
      group.lines.push(line);
      group.pendingLines.push(line);
      group.pendingLineIds.push(line.id);
    } else ungroupedLines.push(line);
  }
  const groups = [...groupsBySupplier.values()];
  for (const group of groups) {
    group.pendingAmount = sumActiveFulfillmentLineAmounts(group.pendingLines);
    // 已定稿或已生效协议的当前应付来自账本，不以明细投影重新计算。
    group.amount = sumFulfillmentAmounts([
      ...group.orders.map(order => workspaceStatus === "draft" && order.status === "draft"
        ? sumActiveFulfillmentLineAmounts(group.lines.filter(line => line.fulfillmentOrderId === order.id))
        : order.currentAmount),
      group.pendingAmount,
    ]);
  }
  return {
    groups,
    ungroupedLines,
    currentAmount: sumFulfillmentAmounts([...groups.map(group => group.amount), ...ungroupedLines.map(line => line.amount)]),
  };
}
